import { useCallback, useEffect, useRef, useState } from "react";
import type { ArticleFilterDto, AuthorsByAlias, SearchArticleMeta } from "../api";
import { getArticleFeedback, giveArticleFeedback, removeArticleFeedback, type SearchArticlesParams, searchArticles } from "../client";
import type { FeedbackType } from "../feedback";
import type { ArticleView } from "../models/article";

const toMessage = (error: unknown): string => (error instanceof Error ? error.message : "Something went wrong");

export type UseArticleListResult = {
  metas: SearchArticleMeta[];
  authors: AuthorsByAlias;
  isLoading: boolean;
  isLoadingMore: boolean;
  hasMore: boolean;
  error: string | null;
  loadMore: () => Promise<void>;
  reload: () => Promise<void>;
};

type ListState = { metas: SearchArticleMeta[]; authors: AuthorsByAlias; cursor: string | null; hasMore: boolean };
const EMPTY: ListState = { metas: [], authors: {}, cursor: null, hasMore: true };

const loadPage = async (params: SearchArticlesParams, from: ListState, append: boolean): Promise<ListState> => {
  const page = await searchArticles({ ...params, cursor: from.cursor ?? undefined });
  return {
    metas: append ? [...from.metas, ...page.metas] : page.metas,
    authors: { ...from.authors, ...page.authors },
    cursor: page.nextCursor,
    hasMore: page.nextCursor !== null,
  };
};

type PageRunner = {
  isLoading: boolean;
  isLoadingMore: boolean;
  error: string | null;
  state: ListState;
  run: (from: ListState, append: boolean) => Promise<void>;
};

/** Runs page loads, dropping the answer of a load that a newer one has replaced. */
const usePageRunner = (view: ArticleView, filter: ArticleFilterDto | undefined, limit: number | undefined): PageRunner => {
  const [state, setState] = useState<ListState>(EMPTY);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const generation = useRef(0);
  const filterRef = useRef(filter);
  filterRef.current = filter;

  const run = useCallback(
    async (from: ListState, append: boolean) => {
      const current = ++generation.current;
      setIsLoading(!append);
      setIsLoadingMore(append);
      try {
        const next = await loadPage({ view, filter: filterRef.current, limit }, from, append);
        if (current === generation.current) {
          setState(next);
          setError(null);
        }
      } catch (e) {
        if (current === generation.current) {
          setError(toMessage(e));
        }
      } finally {
        if (current === generation.current) {
          setIsLoading(false);
          setIsLoadingMore(false);
        }
      }
    },
    [view, limit],
  );

  return { isLoading, isLoadingMore, error, state, run };
};

/**
 * An infinite list over `POST /api/v2/articles/search`. It starts over when `view` or `filterKey` changes;
 * `filterKey` is any string that changes with `filter`, so an inline filter object does not reload on every render.
 */
export const useArticleList = (view: ArticleView, filter: ArticleFilterDto | undefined, filterKey: string, limit?: number): UseArticleListResult => {
  const { isLoading, isLoadingMore, error, state, run } = usePageRunner(view, filter, limit);

  const reload = useCallback(() => run(EMPTY, false), [run]);
  useEffect(() => {
    void reload();
  }, [reload, filterKey]);

  const loadMore = useCallback(async () => {
    if (!isLoading && !isLoadingMore && state.hasMore) {
      await run(state, true);
    }
  }, [run, isLoading, isLoadingMore, state]);

  return { metas: state.metas, authors: state.authors, isLoading, isLoadingMore, hasMore: state.hasMore, error, loadMore, reload };
};

export type UseArticleFeedbackResult = {
  feedbackType: FeedbackType | undefined;
  isLoading: boolean;
  /** Picking the type that is already set removes it. */
  toggle: (type: FeedbackType) => Promise<void>;
};

/** The user's feedback on one article. Anonymous visitors see none. */
export const useArticleFeedback = (alias: string, articleId: string, enabled = true): UseArticleFeedbackResult => {
  const [feedbackType, setFeedbackType] = useState<FeedbackType | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(enabled);

  useEffect(() => {
    if (!enabled) {
      setFeedbackType(undefined);
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    getArticleFeedback(alias, articleId)
      .then(setFeedbackType)
      .catch((e: unknown) => console.error("Failed to load feedback:", e))
      .finally(() => setIsLoading(false));
  }, [alias, articleId, enabled]);

  const toggle = useCallback(
    async (type: FeedbackType) => {
      if (type === feedbackType) {
        await removeArticleFeedback(alias, articleId);
        setFeedbackType(undefined);
        return;
      }
      await giveArticleFeedback(alias, articleId, type);
      setFeedbackType(type);
    },
    [alias, articleId, feedbackType],
  );

  return { feedbackType, isLoading, toggle };
};
